/**
 * Persistent cache for mannequin model assets (GLB files and textures).
 *
 * Stores fetched binaries in IndexedDB so subsequent page loads skip the
 * R2 download entirely. Assets are resolved lazily — only fetched when a
 * variant is first loaded. ETags from the API are stored alongside each
 * asset; on every scene load the cached ETag is compared against the
 * server's current value, and stale entries are automatically replaced.
 *
 * Within a session, resolved blob URLs are held in memory so repeated
 * hook calls are synchronous.
 */

import { useEffect, useState } from "react";
import { apiFetch } from "../api/client.ts";
import { MANNEQUIN_MODELS } from "./registry.ts";
import type { MannequinModel, MannequinTextureUrls } from "./types.ts";

const DB_NAME = "poseref-assets";
const DB_VERSION = 1;
const STORE_NAME = "blobs";

const API_URL: string = (import.meta as any).env?.VITE_API_URL ?? "";

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

interface CachedEntry {
  blob: Blob;
  etag: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      // Drop the old store on version bump to avoid schema conflicts
      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }
      db.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function getEntry(key: string): Promise<CachedEntry | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function putEntry(key: string, entry: CachedEntry): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(entry, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ---------------------------------------------------------------------------
// Asset resolution
// ---------------------------------------------------------------------------

/** Session-lived blob URL cache: stable key → blob URL. */
const blobUrls = new Map<string, string>();

/** Deduplicates concurrent fetches for the same asset. */
const inflight = new Map<string, Promise<string>>();

/**
 * Resolve an asset through the cache stack:
 *   1. In-memory blob URL (instant, same session)
 *   2. IndexedDB entry with matching ETag (no network, cross-session)
 *   3. Remote fetch → store in IDB → create blob URL
 *
 * If the cached ETag doesn't match the server's current ETag, the asset
 * is re-downloaded and the cache entry is replaced.
 */
function resolveAsset(
  stableKey: string,
  remoteUrl: string,
  etag: string,
): Promise<string> {
  // Fast path: already resolved this session (ETag was validated on first resolve)
  const mem = blobUrls.get(stableKey);
  if (mem) return Promise.resolve(mem);

  const existing = inflight.get(stableKey);
  if (existing) return existing;

  const promise = (async () => {
    try {
      // IndexedDB lookup — only use if ETag matches
      const entry = await getEntry(stableKey);
      if (entry && entry.etag === etag) {
        const url = URL.createObjectURL(entry.blob);
        blobUrls.set(stableKey, url);
        return url;
      }

      // Cache miss or stale — fetch from remote
      const res = await fetch(remoteUrl);
      if (!res.ok) throw new Error(`${res.status} fetching ${stableKey}`);
      const blob = await res.blob();

      // Best-effort persist — don't block on IDB failures
      putEntry(stableKey, { blob, etag }).catch(() => {});

      const url = URL.createObjectURL(blob);
      blobUrls.set(stableKey, url);
      return url;
    } finally {
      inflight.delete(stableKey);
    }
  })();

  inflight.set(stableKey, promise);
  return promise;
}

// ---------------------------------------------------------------------------
// Presigned URL + ETag fetching (lazy, per-variant)
// ---------------------------------------------------------------------------

type VariantMeta = {
  glb: string;
  normal: string;
  glbEtag: string;
  normalEtag: string;
};

/** Fetched once per variant per session. */
const variantMetaCache = new Map<string, VariantMeta>();
const variantMetaInflight = new Map<string, Promise<VariantMeta>>();

function fetchVariantMeta(variantId: string): Promise<VariantMeta> {
  const cached = variantMetaCache.get(variantId);
  if (cached) return Promise.resolve(cached);

  const existing = variantMetaInflight.get(variantId);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const data = await apiFetch<Record<string, VariantMeta>>(
        `/api/models/urls?variants=${variantId}`,
      );
      const meta = data[variantId];
      variantMetaCache.set(variantId, meta);
      return meta;
    } finally {
      variantMetaInflight.delete(variantId);
    }
  })();

  variantMetaInflight.set(variantId, promise);
  return promise;
}

// ---------------------------------------------------------------------------
// Stable cache keys (match R2 object keys)
// ---------------------------------------------------------------------------

function stableKey(variantId: string, asset: "glb" | "normal"): string {
  const suffix = variantId.replace("male-", "");
  if (asset === "glb") return `models/mannequin-${suffix}.glb`;
  return `models/mannequin-${suffix}-normal.png`;
}

// ---------------------------------------------------------------------------
// Model resolution
// ---------------------------------------------------------------------------

/**
 * Resolve all assets for a variant through the cache.
 * Compares ETags from the API against cached values to detect stale entries.
 * Returns a MannequinModel whose URLs point to cached blobs.
 */
async function resolveModel(variantId: string): Promise<MannequinModel> {
  const base = MANNEQUIN_MODELS[variantId];

  if (!API_URL) return base; // Local dev — static paths via Vite

  let meta: VariantMeta;
  try {
    meta = await fetchVariantMeta(variantId);
  } catch {
    return base; // API unavailable — fall back to static paths
  }

  const glbUrl = await resolveAsset(
    stableKey(variantId, "glb"),
    meta.glb,
    meta.glbEtag,
  );

  let textures: MannequinTextureUrls | undefined;
  if (base.textures?.normal) {
    const normalUrl = await resolveAsset(
      stableKey(variantId, "normal"),
      meta.normal,
      meta.normalEtag,
    );
    textures = { ...base.textures, normal: normalUrl };
  } else {
    textures = base.textures;
  }

  return { ...base, url: glbUrl, textures };
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

/**
 * Resolve a mannequin variant's assets through the persistent cache.
 * Returns `null` while the first download is in progress, then a
 * `MannequinModel` with blob URLs for all subsequent renders.
 *
 * On each mount (scene load), ETags are checked against the server.
 * If the model has changed upstream, the cache is refreshed automatically.
 */
export function useCachedModel(modelId: string): MannequinModel | null {
  const [model, setModel] = useState<MannequinModel | null>(() => {
    // Synchronous fast-path: local dev
    if (!API_URL) return MANNEQUIN_MODELS[modelId] ?? null;
    // If blob URLs are in memory, the ETag was already validated this session
    const glb = blobUrls.get(stableKey(modelId, "glb"));
    if (!glb) return null;

    const base = MANNEQUIN_MODELS[modelId];
    const normal = blobUrls.get(stableKey(modelId, "normal"));
    const textures =
      base.textures?.normal && normal
        ? { ...base.textures, normal }
        : base.textures;
    return { ...base, url: glb, textures };
  });

  useEffect(() => {
    if (!API_URL) return;
    let cancelled = false;
    resolveModel(modelId).then((resolved) => {
      if (!cancelled) setModel(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  return model;
}
